import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "~/hooks/use-media-query";
import { useProductManager } from "~/managers/use-product-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useProductStore } from "~/stores/use-product-store";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import { ProductForm } from "./product-form";

export default function ProductDialog() {
  const {
    productFormOpen,
    currentProduct,
    setProductFormOpen,
    setCurrentProduct,
  } = useProductStore();

  const { deleteProduct } = useProductManager();

  const { checkUnsavedChanges } = useUnsavedChanges();

  const { t } = useTranslation("common");

  const isMobile = useMediaQuery(768);
  const handleDialog = (open: boolean, force?: boolean) => {
    if (open === false) {
      if (force !== true) {
        const result = checkUnsavedChanges();
        if (result === true) return;
      }
      setCurrentProduct(undefined);
    }
    setProductFormOpen(open);
  };

  const handleDelete = async () => {
    if (!currentProduct) return;
    const result = await deleteProduct(currentProduct.id);
    if (result === true) {
      setProductFormOpen(false);
      setCurrentProduct(undefined);
    }
  };

  return (
    <>
      {isMobile && (
        <Drawer
          open={productFormOpen}
          onClose={(preventClose) => {
            if (preventClose) {
              const result = checkUnsavedChanges();
              if (result === true) preventClose();
            }
          }}
          onOpenChange={handleDialog}
        >
          <DrawerContent>
            <DrawerHeader showClose={true} onClose={() => handleDialog(false)}>
              <DrawerTitle>{t("Product_product")}</DrawerTitle>
            </DrawerHeader>
            <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
              <ProductForm
                onClose={(force) => handleDialog(false, force)}
                product={currentProduct}
              />
            </div>
            <DrawerFooter>
              <div className="flex flex-col gap-2">
                <Button type="submit" form="product-form" variant="success">
                  {currentProduct ? t("update") : t("create")}
                </Button>
                {currentProduct && (
                  <Button
                    onClick={handleDelete}
                    className="text-red-400 hover:text-red-500"
                    variant="ghost"
                  >
                    {t("delete_product")}
                  </Button>
                )}
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}

      {!isMobile && (
        <Dialog open={productFormOpen} onOpenChange={handleDialog}>
          <DialogContent className="max-w-2xl w-full">
            <DialogHeader>
              <DialogTitle>{t("Product_product")}</DialogTitle>
              <VisuallyHidden>
                <DialogDescription></DialogDescription>
              </VisuallyHidden>
            </DialogHeader>
            <div className="max-h-[80vh] p-4 overflow-auto small-scrollbar">
              <ProductForm
                onClose={(force) => handleDialog(false, force)}
                product={currentProduct}
              />
            </div>
            <DialogFooter>
              <Button type="submit" form="product-form" variant="success">
                {currentProduct ? t("update") : t("create")}
              </Button>
              {currentProduct && (
                <Button
                  onClick={handleDelete}
                  className="text-red-400 hover:text-red-500"
                  variant="ghost"
                >
                  {t("delete_product")}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
